import { config } from '~/config/config';

/** Rendition of a Product Photo on the Content CDN; pick by how large the photo is displayed. */
export type PhotoSize = 'small' | 'big' | 'maxi';

/** Site-local (`/images/...`) or absolute (`https://...`) values are used as they are. */
function isOutsideContentCdn(value: string): boolean {
  return value.startsWith('/') || /^https?:\/\//.test(value);
}

/**
 * URL of a Product Photo in the given size. The catalog stores Content CDN paths without a size
 * (`25720/20617`) and the bucket's base URL comes from the environment config — see PDR-0018.
 * The CDN holds every rendition as PNG and WebP; the WebP is 10–20× smaller (`_big`: 25 kB vs 460 kB),
 * checked for all 344 catalog photos in all three sizes on 2026-10-27.
 */
export function productPhotoUrl(photo: string | undefined, size: PhotoSize): string | undefined {
  if (!photo || isOutsideContentCdn(photo)) {
    return photo;
  }

  return `${config.contentCdnUrl}/${photo}_${size}.webp`;
}

/**
 * `srcset` that loads `size` on standard displays and `retinaSize` on high-density ones, where the smaller
 * rendition shows blurry (`big` is 385 px wide, the detail photo box 380 CSS px). Undefined for photos
 * outside the Content CDN, which have only one rendition.
 */
export function productPhotoSrcSet(
  photo: string | undefined,
  size: PhotoSize,
  retinaSize: PhotoSize,
): string | undefined {
  if (!photo || isOutsideContentCdn(photo)) {
    return undefined;
  }

  return `${productPhotoUrl(photo, size)} 1x, ${productPhotoUrl(photo, retinaSize)} 2x`;
}

/** URL of a Product Document stored as a Content CDN path (`25720/manual/AC-160-C_EN_MLZ54606.pdf`). */
export function productDocumentUrl(path: string): string {
  if (isOutsideContentCdn(path)) {
    return path;
  }

  return `${config.contentCdnUrl}/${path}`;
}
